/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import { isFinite } from 'lodash';
import i18n from '../../i18n';
import { Property } from '../../types';
import { CountriesWithCE, CountriesWithPAC, CountryKeys } from '../../map/constants/MapStyles';
import { getFullAddressStr, getFullAddressStr_CE_NetharLands, getFullAddressStr_UK } from '..';
import { addressKeys, PropertyTypes } from '../../data/constants';

/// //////// sub utility ///////////
function checkValidNumber(value: string) {
  const fvalue = parseFloat(value);
  if (!Number.isNaN(fvalue) && isFinite(fvalue)) {
    return true;
  }
  return false;
}

/**
 * Per UK, the availabilty name header is 'Floors' instead of 'Availability Name'.
 * @param surveyCountry
 * @returns
 */
export function getAvailabilityNameHeader(surveyCountry: string) {
  if (surveyCountry === CountryKeys.UK) {
    return i18n.t('columnHeaders.availability_floors');
  }
  return i18n.t('columnHeaders.availability_name');
}

// return false if the survey country is AU or NZ, and the property type is Industrial
export function isAvailabilityNameApplicable(surveyCountry: string, propertyType: string) {
  if (CountriesWithPAC.includes(surveyCountry)) {
    return propertyType !== PropertyTypes.Industrial;
  }

  if (CountriesWithCE.includes(surveyCountry)) {
    return propertyType !== PropertyTypes.Retail;
  }

  return true;
}

export function dateFormatter(data: any) {
  if (data?.includes('T') && data?.includes('Z')) {
    const Value = data?.sOlice(0, data.indexOf('T'));
    const dateParts = Value?.split('-');
    const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
    const dateFormat = `${
      date.getMonth() + 1
    }/${date.getDate()}/${date.getFullYear()}`;

    data = dateFormat;
    return data;
  }

  // return strings those are not date strings
  return data;
}

function getDecorator(attrKey: string) {
  let prefix = '';
  let suffix = '';
  let possibleInteger = true;

  if (['askingRent', 'askingPrice'].includes(attrKey)) {
    prefix = '$';
    suffix = '/SF';
    possibleInteger = false;
  } else if (
    [
      'minTotalAvailableSpace',
      'maxTotalAvailableSpace',
      'totalAvailableSpace',
      'propertySize',
      'netRentableArea',
      'typicalFloorSize',
      'vacantSpace',
    ].includes(attrKey)
  ) {
    suffix = ' SF';
  } else if (['vacantPercentage'].includes(attrKey)) {
    suffix = '%';
    possibleInteger = false;
  } else if (['opex'].includes(attrKey)) {
    prefix = '$';
    possibleInteger = false;
  } else {
    return;
  }

  return {
    prefix,
    suffix,
    possibleInteger,
    fractionDigits: possibleInteger ? 1 : 2,
  };
}

export function refineNumberString(
  value: string,
  possibleInteger: boolean,
  fractionDigits: number,
): string {
  if (!value) return '';
  const fvalue = parseFloat(value);

  if (possibleInteger) {
    const nvalue = parseInt(value, 10);
    if (fvalue === nvalue) {
      return nvalue.toLocaleString('en-US');
    }
  }

  const localeStringOpts = {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  };
  return fvalue.toLocaleString('en-US', localeStringOpts);
}

export function getValidNumberString(
  _value: string,
  possibleInteger: boolean,
  fractionDigits: number,
): string {
  const value = _value || ''; // .trim()

  const others = value.match(/[^0-9.,]/g) || [];
  if (others.length > 0) {
    return '';
  }

  const svalue = value.replace(/[^0-9.,]/g, '');
  if (!checkValidNumber(svalue)) {
    return '';
  }
  return refineNumberString(svalue, possibleInteger, fractionDigits);
}

export function getLocaleStringForValue(
  value: string,
  possibleInteger: boolean,
  fractionDigits: number,
): string {
  const tempVal = value ? value?.replace(/,/g, '') : '';
  const refinedString = getValidNumberString(
    tempVal,
    possibleInteger,
    fractionDigits,
  );
  return refinedString || value;
}
const getSingletonValue = (attrKey: string, value: string) => {
  const decorator = getDecorator(attrKey);
  if (!decorator) {
    return value;
  }
  const { prefix, suffix, possibleInteger, fractionDigits } = decorator;

  if (value) {
    // value = value ? value?.replace(/,/g, '') : '';
    const svalue = getValidNumberString(
      value ? value?.replace(/,/g, '') : '',
      possibleInteger,
      fractionDigits,
    );
    if (svalue) {
      return `${prefix}${svalue}${suffix}`;
    }
    return value;
  }

  return '';
};

export function getTotalAvailableValue(min: string, max: string, value: string) {
  const attrKey = 'totalAvailableSpace';

  let svalue = getSingletonValue(attrKey, value);
  if (svalue) {
    return svalue;
  }

  // formula based generation
  const decorator = getDecorator(attrKey)!;
  const { prefix, suffix, possibleInteger, fractionDigits } = decorator;

  min = min ? min?.replace(/,/g, '') : '';
  max = max ? max?.replace(/,/g, '') : '';

  const smin = getValidNumberString(min, possibleInteger, fractionDigits);
  const smax = getValidNumberString(max, possibleInteger, fractionDigits);

  svalue = '';

  if (smin) {
    if (smax) {
      svalue = `${smin} - ${smax}`;
    } else {
      svalue = `${smin}`;
    }
  } else if (smax) {
    svalue = `${smax}`;
  }

  if (svalue) {
    return `${prefix}${svalue}${suffix}`;
  }
  return '';
}

export function addNewLine(value: string) {
  if (!value) return '';
  const match: any = value.match(/, \d/);
  if (match && match.index !== -1 && value.endsWith('SF')) {
    return `${value.substring(0, match.index + 1)}\r\n${value.substring(
      match.index + 1,
      value.length,
    )}`;
  }
  return value;
}

export const getAddressDisplay = (property: Property, surveyCountry: string): string => {
  switch (surveyCountry?.trim()) {
    case CountryKeys.NL:
      return getFullAddressStr_CE_NetharLands(
        property.address1,
        property.postalCode,
        property.city,
        property.state,
      );
    case CountryKeys.UK:
      return getFullAddressStr_UK(
        property.address1,
        property.city,
        property.postalCode,
      );
    default:
      return getFullAddressStr(
        property.address1,
        property.city,
        property.state,
        property.postalCode,
      );
  }
};

/// ////////
export function getPropertyValueForDisplay(
  attrKey: string,
  property: Property,
) {
  if (!property) return '';
  return property[attrKey] || property.data?.[attrKey];
}

export function getPropertyValueForDisplay2(
  attrKey: string,
  property: Property,
) {
  return getPropertyValueForDisplay(attrKey, property);
}
export function filterPropertiesByFieldRules(
  properties: Property[],
  surveyPropertyFieldRules: Record<string, any>[],
  fieldsToFilter: string[] = addressKeys,
): Property[] {
  return properties?.map((property: Property) => {
    const updatedProperty = { ...property };

    fieldsToFilter.forEach((key) => {
      const rule = surveyPropertyFieldRules?.find(
        (fieldRule: Record<string, any>) => fieldRule.fieldKey === key,
      );

      if (rule?.status !== 'ACTIVE') {
        updatedProperty[key] = '';
      }
    });

    return updatedProperty;
  }) || [];
}

export function getAvailabilityFieldValues(
  fieldKey: string,
  availability: any,
): string {
  // Validate input
  if (!availability || typeof availability !== 'object') {
    return '-';
  }

  // Safely access the field value
  const value = availability[fieldKey] || availability.data?.[fieldKey];

  // Return the value or fallback
  return value !== undefined ? value : '-';
}

export function getAvailabilityNameValue(availability: any) {
  // Import AVAILABILITY_NAME_KEY locally to avoid circular dependencies
  const AVAILABILITY_NAME_KEY = 'availabilityName';
  const name = availability[AVAILABILITY_NAME_KEY]?.replace(/, /g, ',\r\n') || '-';
  return name;
}

export const getPropertyDisplayName = (property: Property, propertyType: string, surveyCountry: string) => {
  const shouldDisplayName = (propertyType === PropertyTypes.Industrial &&
    [CountryKeys.HU, CountryKeys.PL, CountryKeys.NL, CountryKeys.IT,
      CountryKeys.PU, CountryKeys.FI, CountryKeys.ES, CountryKeys.DE,
      CountryKeys.RO].includes(surveyCountry));

  return shouldDisplayName ? (property.name || property.address1 || '-') : (property.address1 || '-');
};
